import { get } from '~shared/utils/http';
import { buildSpectateUrl } from '~spa/Utils/Spectate';
import {
    mergeMarkets,
    getMarketBetable,
} from '~shared/utils/market-utils';
import {
    selectionArrayToObject,
    updateSelection,
} from '~shared/utils/selections-utils';

export const getFeaturedInplayEvents = async (sportSlug) => {
    const sportFilter = sportSlug ? `sport=${sportSlug}` : '';
    const response = await get(buildSpectateUrl(`/inplay-req/featured?${sportFilter}`));

    if (!response.ok) {
        return {};
    }

    return response.json();
};

/**
 * get an array of objects hydrated by event_id (which is the key of the input object)
 *
 * @param {Array} events Array of event objects received from API
 * @returns {Array} payload of {event_id: 1, markets: [{id: 1}, {id: 2}]}
 */
export const formatInplayWidgetWsParams = (events = []) => {
    const requestPackets = events.map(({id:event_id, market_id, grouped_market_ids: groupedMarkets = []}) => {
        const requestPacket = groupedMarkets.length
            ? groupedMarkets.map(market_id => ({
                    event_id,
                    market_id,
            }))
            : { event_id, market_id };

        return requestPacket;
    });

    return [].concat(...requestPackets);
};

export const updateEvents = (newEvent, {events = [], markets = {}}) => {
    const {
        id: eventId = null,
        suspended: newEventSuspended = null,
        competitors: newCompetitors = null,
        extra_info: newExtraInfo = null,
    } = newEvent;

    return events.map(event => {
        if (event.id === eventId) {
            const {
                suspended: eventSuspended = null,
                competitors,
                extra_info = {},
            } = event;
            const allMarketsSuspended = Object.values(markets[eventId]).every(({suspended = true}) => suspended);

            if (newExtraInfo) {
                newEvent.extra_info = {
                    ...extra_info,
                    ...newExtraInfo,
                };
            }

            return {
                ...event,
                ...newEvent,
                suspended: allMarketsSuspended || newEventSuspended || eventSuspended,
                ...newCompetitors && {
                    competitors: competitors.map((item, index) => ({...item, ...newCompetitors[index]})),
                },
            };
        }

        return event;
    });
};

export const updatePrices = (newEvent, {event, selections = []}) => {
    const {
        id: eventId,
        markets: newMarkets = [],
    } = newEvent;
    const {
        market_id: marketId,
    } = event;
    const eventSelections = selections[eventId] ?? [];
    const newMarket = newMarkets.find(({ id, selections }) => (id === marketId && selections));
    let newSelections = null;

    if(newMarket) {
        if (Array.isArray(newMarket.selections)) {
            newSelections = newMarket.selections.reduce((acc, selection) => ({
                ...acc,
                [selection.id]: selection,
            }), {});
        } else {
            newSelections = newMarket.selections;
        }
    }

    // Update if event has selections
    if(eventSelections.length) {
        const updatedSelections = eventSelections.map(item => {
            if (!item) {
                const existingSelectionIds = eventSelections.map(({id}) => id);
                const id = Object.keys(newSelections ?? {}).find(key => !existingSelectionIds.includes(key));

                return newSelections[id];
            }

            return { ...item, ...(newSelections && { ...newSelections[item.id] }) };
        });

        return { ...selections, [eventId]: updatedSelections };
    }

    return selections;
};

export const updatePricesGameLine = (newEvent, {event, markets = {}, selections = {}}) => {
    const {
        id: eventId,
        markets: newMarkets = [],
    } = newEvent;
    const {
        grouped_market_ids: groupedMarketIds = [],
    } = event;
    const eventSelections = selections[eventId] ?? {};
    const eventMarkets = markets[eventId] ?? {};
    const normalizedNewMarkets = mergeMarkets(newMarkets);
    const marketUpdates = groupedMarketIds.map(marketId => normalizedNewMarkets.find(market => market.id === marketId)).filter(Boolean);

    marketUpdates.forEach(({
        id: marketId,
        selections: newSelections,
        default_line_selections: newDefaultLineSelections = [],
        ...updatedMarket
    }) => {
        const marketSelections = selectionArrayToObject(eventSelections[marketId]) || {};
        const {
            default_line_selections: currentDefaultLineSelections = [],
        } = eventMarkets[marketId] || {};
        const defaultLineSelections = newDefaultLineSelections.length ? newDefaultLineSelections : currentDefaultLineSelections;
        const isMarketBetable = getMarketBetable(updatedMarket);
        // Inplay updates are formatted differently from price updates.
        const updatedSelections = Array.isArray(newSelections) ? selectionArrayToObject(newSelections) : newSelections;

        eventSelections[marketId] = defaultLineSelections.map(selectionId => {
            const currentSelection = marketSelections[selectionId] || {};
            const updatedSelection = updatedSelections[selectionId] || false;

            if (!updatedSelection) {
                return currentSelection;
            }

            return updateSelection(currentSelection, {id: selectionId, ...updatedSelection }, isMarketBetable);
        });

        eventMarkets[marketId] = {
            id: marketId,
            ...updatedMarket,
            default_line_selections: defaultLineSelections,
        };
    });

    return {
        gameLineMarkets: { ...markets, [eventId]: eventMarkets },
        gameLineSelections: { ...selections, [eventId]: eventSelections },
    };
};
